import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { soundService } from '../services/soundService';

interface OnboardingStep {
  id: string;
  target: string;
  title: string;
  description: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  icon?: string;
}

interface OnboardingOverlayProps {
  onComplete: () => void;
  onSkip: () => void;
}

const OnboardingOverlay: React.FC<OnboardingOverlayProps> = ({ onComplete, onSkip }) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(-1); // -1 for intro animation
  const [showIntro, setShowIntro] = useState(true);
  const [introText, setIntroText] = useState('');
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);

  // Define tutorial steps
  const steps: OnboardingStep[] = [
    {
      id: 'stats',
      target: '[data-onboarding="stats-panel"]',
      title: t.onboarding.steps.stats.title,
      description: t.onboarding.steps.stats.description,
      position: 'right',
      icon: '📊',
    },
    {
      id: 'quests',
      target: '[data-onboarding="quest-buttons"]',
      title: t.onboarding.steps.quests.title,
      description: t.onboarding.steps.quests.description,
      position: 'bottom',
      icon: '⚔️',
    },
    {
      id: 'ai-generator',
      target: '[data-onboarding="ai-generator"]',
      title: t.onboarding.steps.aiGenerator.title,
      description: t.onboarding.steps.aiGenerator.description,
      position: 'bottom',
      icon: '🤖',
    },
    {
      id: 'sidebar',
      target: '[data-onboarding="sidebar"]',
      title: t.onboarding.steps.sidebar.title,
      description: t.onboarding.steps.sidebar.description,
      position: 'right',
      icon: '🗺️',
    },
    {
      id: 'level',
      target: '[data-onboarding="level-info"]',
      title: t.onboarding.steps.level.title,
      description: t.onboarding.steps.level.description,
      position: 'bottom',
      icon: '⭐',
    },
  ];

  // Epic intro animation
  useEffect(() => {
    if (showIntro) {
      // Play the onboarding tutorial voice
      soundService.onboardingIntro();
      
      const fullText = t.onboarding.intro.message;
      let index = 0;
      const typingSpeed = 65; // Slowed down to match voice-over better

      const interval = setInterval(() => {
        if (index < fullText.length) {
          setIntroText(fullText.slice(0, index + 1));
          index++;
        } else {
          clearInterval(interval);
          // Wait a moment before starting tutorial
          setTimeout(() => {
            setShowIntro(false);
            setCurrentStep(0);
          }, 2000);
        }
      }, typingSpeed);

      return () => {
        clearInterval(interval);
        // Stop voice-over if user navigates away during intro
        soundService.stopOnboardingVoice();
      };
    }
  }, [showIntro, t.onboarding.intro.message]);

  // Update highlight position when step changes
  useEffect(() => {
    // Remove highlight class from previous element
    const allHighlightedElements = document.querySelectorAll('.onboarding-highlighted');
    allHighlightedElements.forEach(el => {
      el.classList.remove('onboarding-highlighted');
      (el as HTMLElement).style.position = '';
      (el as HTMLElement).style.zIndex = '';
    });

    if (currentStep >= 0 && currentStep < steps.length) {
      const element = document.querySelector(steps[currentStep].target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setHighlightRect(rect);
        
        // Add highlight class and boost z-index to be ABOVE overlay
        element.classList.add('onboarding-highlighted');
        const originalPosition = window.getComputedStyle(element).position;
        if (originalPosition === 'static') {
          (element as HTMLElement).style.position = 'relative';
        }
        (element as HTMLElement).style.zIndex = '102';
      }
    } else {
      setHighlightRect(null);
    }

    // Cleanup on unmount
    return () => {
      const allHighlightedElements = document.querySelectorAll('.onboarding-highlighted');
      allHighlightedElements.forEach(el => {
        el.classList.remove('onboarding-highlighted');
        (el as HTMLElement).style.position = '';
        (el as HTMLElement).style.zIndex = '';
      });
    };
  }, [currentStep, steps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkipIntro = () => {
    soundService.stopOnboardingVoice();
    setShowIntro(false);
    setCurrentStep(0);
  };

  const handleSkipTutorial = () => {
    soundService.stopOnboardingVoice();
    onSkip();
  };

  // Calculate tooltip position with viewport bounds checking
  const getTooltipPosition = () => {
    if (!highlightRect) return {};
    
    const step = steps[currentStep];
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 768; // md breakpoint
    const isTablet = viewportWidth >= 768 && viewportWidth < 1024; // between md and lg
    
    // On mobile, always center tooltip at bottom of screen for best UX
    if (isMobile) {
      return {
        left: '50%',
        bottom: '20px',
        transform: 'translateX(-50%)',
        maxWidth: 'calc(100vw - 32px)', // 16px padding on each side
        actualPosition: 'bottom'
      };
    }
    
    const padding = isTablet ? 16 : 20;
    const tooltipWidth = isTablet ? 320 : 400;
    const tooltipHeight = 300;
    
    let position: any = {};
    let preferredPosition = step.position;
    
    // Check if preferred position would go off-screen and adjust
    if (preferredPosition === 'right' && highlightRect.right + padding + tooltipWidth > viewportWidth) {
      preferredPosition = 'left';
    }
    if (preferredPosition === 'left' && highlightRect.left - padding - tooltipWidth < 0) {
      preferredPosition = 'right';
    }
    if (preferredPosition === 'bottom' && highlightRect.bottom + padding + tooltipHeight > viewportHeight) {
      preferredPosition = 'top';
    }
    if (preferredPosition === 'top' && highlightRect.top - padding - tooltipHeight < 0) {
      preferredPosition = 'bottom';
    }
    
    // If still would go off-screen horizontally, use bottom/top and center
    if ((preferredPosition === 'right' && highlightRect.right + padding + tooltipWidth > viewportWidth) ||
        (preferredPosition === 'left' && highlightRect.left - padding - tooltipWidth < 0)) {
      preferredPosition = 'bottom';
    }
    
    switch (preferredPosition) {
      case 'right':
        position = {
          left: `${Math.min(highlightRect.right + padding, viewportWidth - tooltipWidth - 20)}px`,
          top: `${highlightRect.top + highlightRect.height / 2}px`,
          transform: 'translateY(-50%)',
          maxWidth: isTablet ? '320px' : '400px',
        };
        break;
      case 'bottom':
        position = {
          left: '50%',
          top: `${Math.min(highlightRect.bottom + padding, viewportHeight - tooltipHeight - 20)}px`,
          transform: 'translateX(-50%)',
          maxWidth: isTablet ? 'calc(100vw - 32px)' : '90vw',
        };
        break;
      case 'top':
        position = {
          left: '50%',
          bottom: `${viewportHeight - highlightRect.top + padding}px`,
          transform: 'translateX(-50%)',
          maxWidth: isTablet ? 'calc(100vw - 32px)' : '90vw',
        };
        break;
      case 'left':
        position = {
          right: `${Math.min(viewportWidth - highlightRect.left + padding, viewportWidth - 100)}px`,
          top: `${highlightRect.top + highlightRect.height / 2}px`,
          transform: 'translateY(-50%)',
          maxWidth: isTablet ? '320px' : '400px',
        };
        break;
      default:
        position = {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        };
    }
    
    return { ...position, actualPosition: preferredPosition };
  };

  // Render intro animation
  if (showIntro) {
    return (
      <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-8 text-center">
          {/* System Initialization */}
          <div className="mb-6 sm:mb-8 animate-fade-in">
            <div className="text-4xl sm:text-5xl md:text-6xl mb-4 sm:mb-6 animate-float">⚔️</div>
            <h1 className="font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-system-blue mb-3 sm:mb-4 tracking-wider animate-glow px-2">
              {t.onboarding.intro.title}
            </h1>
            <div className="h-1 w-48 sm:w-64 mx-auto bg-gradient-to-r from-transparent via-system-blue to-transparent mb-6 sm:mb-8"></div>
          </div>

          {/* Typing text */}
          <div className="font-rajdhani text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed mb-8 sm:mb-12 min-h-[100px] sm:min-h-[120px] px-2">
            {introText}
            <span className="inline-block w-1.5 sm:w-2 h-5 sm:h-6 bg-system-blue ml-1 animate-pulse"></span>
          </div>

          {/* Skip button */}
          <button
            onClick={handleSkipIntro}
            className="text-gray-500 hover:text-gray-300 text-sm sm:text-base font-rajdhani uppercase tracking-wider transition-colors px-4 py-2 active:scale-95"
          >
            {t.onboarding.intro.skip} →
          </button>
        </div>
      </div>
    );
  }

  // Render tutorial steps
  const isMobile = window.innerWidth < 768;
  const spotlightPadding = isMobile ? 8 : 12; // Less padding on mobile for better visibility
  const overlayOpacity = isMobile ? 'bg-black/80' : 'bg-black/90'; // Slightly less dark on mobile
  
  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {/* Dark overlay with spotlight cutout */}
      <div className="absolute inset-0 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
        {/* Spotlight cutout - ensure highlighted area is NEVER covered */}
        {highlightRect && (
          <>
            {/* Top overlay */}
            <div 
              className={`absolute inset-x-0 ${overlayOpacity} pointer-events-auto`}
              style={{
                top: 0,
                height: `${Math.max(0, highlightRect.top - spotlightPadding)}px`,
              }}
            />
            {/* Bottom overlay */}
            <div 
              className={`absolute inset-x-0 ${overlayOpacity} pointer-events-auto`}
              style={{
                top: `${highlightRect.bottom + spotlightPadding}px`,
                bottom: 0,
              }}
            />
            {/* Left overlay */}
            <div 
              className={`absolute ${overlayOpacity} pointer-events-auto`}
              style={{
                top: `${Math.max(0, highlightRect.top - spotlightPadding)}px`,
                left: 0,
                width: `${Math.max(0, highlightRect.left - spotlightPadding)}px`,
                height: `${highlightRect.height + (spotlightPadding * 2)}px`,
              }}
            />
            {/* Right overlay */}
            <div 
              className={`absolute ${overlayOpacity} pointer-events-auto`}
              style={{
                top: `${Math.max(0, highlightRect.top - spotlightPadding)}px`,
                left: `${highlightRect.right + spotlightPadding}px`,
                right: 0,
                height: `${highlightRect.height + (spotlightPadding * 2)}px`,
              }}
            />
            {/* Glowing border around highlighted area - positioned ABOVE overlays */}
            <div
              className={`absolute ${isMobile ? 'border-2' : 'border-4'} border-system-blue rounded-lg pointer-events-none z-[102]`}
              style={{
                left: `${highlightRect.left - spotlightPadding}px`,
                top: `${highlightRect.top - spotlightPadding}px`,
                width: `${highlightRect.width + (spotlightPadding * 2)}px`,
                height: `${highlightRect.height + (spotlightPadding * 2)}px`,
                boxShadow: isMobile 
                  ? '0 0 30px rgba(59, 130, 246, 0.8), 0 0 60px rgba(59, 130, 246, 0.4)'
                  : '0 0 50px rgba(59, 130, 246, 1), 0 0 100px rgba(59, 130, 246, 0.5), inset 0 0 30px rgba(59, 130, 246, 0.3)',
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }}
            />
          </>
        )}
      </div>

      {/* Tooltip */}
      {currentStep >= 0 && currentStep < steps.length && (() => {
        const positionStyle = getTooltipPosition();
        const actualPosition = (positionStyle as any).actualPosition;
        delete (positionStyle as any).actualPosition;
        
        return (
          <div
            className="absolute pointer-events-auto animate-fade-in px-4 md:px-0"
            style={positionStyle}
          >
            <div className="bg-system-darker border-2 border-system-blue rounded-lg p-4 sm:p-5 md:p-6 shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <span className="text-2xl sm:text-3xl flex-shrink-0">{steps[currentStep].icon}</span>
              <h3 className="font-orbitron text-base sm:text-lg md:text-xl font-bold text-system-blue uppercase leading-tight">
                {steps[currentStep].title}
              </h3>
            </div>

            {/* Description */}
            <p className="text-gray-300 font-rajdhani text-sm sm:text-base leading-relaxed mb-4 sm:mb-5 md:mb-6">
              {steps[currentStep].description}
            </p>

            {/* Progress */}
            <div className="mb-3 sm:mb-4">
              <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-500 font-rajdhani mb-1.5 sm:mb-2">
                <span>{t.common.step} {currentStep + 1} {t.common.of} {steps.length}</span>
                <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
              </div>
              <div className="w-full h-1 sm:h-1.5 bg-system-dark rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-system-blue to-purple-500 transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3">
              <button
                onClick={handleSkipTutorial}
                className="text-gray-500 hover:text-gray-300 active:scale-95 text-xs sm:text-sm font-rajdhani uppercase tracking-wider transition-all py-2 sm:py-0 order-2 sm:order-1"
              >
                {t.onboarding.actions.skip}
              </button>
              
              <div className="flex gap-2 order-1 sm:order-2">
                {currentStep > 0 && (
                  <button
                    onClick={handlePrevious}
                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-2.5 bg-system-dark border border-system-blue/30 rounded text-gray-300 hover:border-system-blue hover:text-white active:scale-95 font-rajdhani text-xs sm:text-sm uppercase tracking-wider transition-all touch-manipulation"
                  >
                    ← {t.common.back}
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-2.5 bg-system-blue border border-system-blue rounded text-white hover:bg-blue-600 active:scale-95 font-orbitron text-xs sm:text-sm font-bold uppercase tracking-wider transition-all shadow-neon-blue touch-manipulation"
                >
                  {currentStep < steps.length - 1 ? `${t.common.next} →` : t.onboarding.actions.finish}
                </button>
              </div>
            </div>
          </div>

          {/* Arrow indicator - Hidden on mobile */}
          {!isMobile && (
            <div
              className="absolute w-0 h-0 pointer-events-none"
              style={{
                ...(actualPosition === 'right' && {
                  right: '100%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  borderTop: '12px solid transparent',
                  borderBottom: '12px solid transparent',
                  borderRight: '12px solid rgb(30, 41, 59)',
                }),
                ...(actualPosition === 'bottom' && {
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderBottom: '12px solid rgb(30, 41, 59)',
                }),
                ...(actualPosition === 'top' && {
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderTop: '12px solid rgb(30, 41, 59)',
                }),
                ...(actualPosition === 'left' && {
                  left: '100%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  borderTop: '12px solid transparent',
                  borderBottom: '12px solid transparent',
                  borderLeft: '12px solid rgb(30, 41, 59)',
                }),
              }}
            />
          )}
        </div>
        );
      })()}
    </div>
  );
};

export default OnboardingOverlay;

